// ConstruireEvaluationRich.jsx - Constructeur d'évaluation enrichie (EvaluationRich)
// Types de question supportés (QuestionQCMRich.type) :
//   CHOIX_UNIQUE, CHOIX_MULTIPLE, VRAI_FAUX, TEXTE_TROU,
//   APPARIEMENT, CLASSEMENT, ORDONNANCEMENT,
//   REP_COURT, REP_LONG, REP_NUMERIQUE, CODE,
//   ZONE_TEXTE_RICHE, QUESTION_OUVERTE, DEPOT_FICHIER
//
// NB : DEPOT_FICHIER (ex-"AUDIO/FILE") sert quand c'est l'ÉTUDIANT qui produit
// un fichier (oral enregistré, scan, rendu de projet). Ce n'est PAS le type à
// utiliser pour "faire écouter un audio puis poser une question dessus" — dans
// ce cas, choisissez le type de question adapté (CHOIX_UNIQUE, REP_COURT,
// QUESTION_OUVERTE...) et attachez l'audio via le champ média ci-dessous
// (mediaType: AUDIO/IMAGE/VIDEO/PDF). Le média est l'entrée, le type de
// question est la sortie : les deux sont indépendants et se combinent
// librement sur n'importe quel type de question.
//
// Endpoints backend utilisés (voir AdminController.java) :
//   GET    /api/v1/admin/evaluations-rich/{id}
//   POST   /api/v1/admin/evaluations-rich
//   PUT    /api/v1/admin/evaluations-rich/{id}
//   DELETE /api/v1/admin/evaluations-rich/{id}
//   PATCH  /api/v1/admin/evaluations-rich/{id}/status?status=...
import React, { useState, useCallback, useEffect } from 'react';
import {
    Plus, Trash2, ChevronDown, ChevronRight, ArrowUp, ArrowDown,
    X, Eye, Save, BarChart3, ClipboardList, HelpCircle
} from 'lucide-react';

// Types d'évaluation enrichie (cf. EvaluationRich.java : "DIAGNOSTIC, SOMMATIVE, QUIZ")
const EVAL_TYPES = ['DIAGNOSTIC', 'SOMMATIVE', 'QUIZ'];
const EVAL_STATUSES = ['BROUILLON', 'PUBLIEE', 'TERMINEE', 'ARCHIVEE'];

const CODE_LANGUAGES = ['javascript', 'python', 'java', 'c', 'cpp', 'csharp', 'sql', 'html', 'css', 'typescript', 'php', 'autre'];

// Types dont la correction automatique par QuestionQCMRich.evaluerReponse() est fiable par défaut
const AUTO_DEFAULT_TYPES = ['CHOIX_UNIQUE', 'CHOIX_MULTIPLE', 'VRAI_FAUX', 'TEXTE_TROU', 'APPARIEMENT', 'CLASSEMENT', 'ORDONNANCEMENT', 'REP_NUMERIQUE', 'REP_COURT'];

const QUESTION_TYPES_RICH = [
    { value: 'CHOIX_UNIQUE', label: 'Choix unique', icon: '🔘', category: 'Choix' },
    { value: 'CHOIX_MULTIPLE', label: 'Choix multiple', icon: '☑️', category: 'Choix' },
    { value: 'VRAI_FAUX', label: 'Vrai / Faux', icon: '✅', category: 'Choix' },
    { value: 'TEXTE_TROU', label: 'Texte à trous', icon: '📝', category: 'Texte' },
    { value: 'APPARIEMENT', label: 'Appariement', icon: '🔗', category: 'Association' },
    { value: 'CLASSEMENT', label: 'Classement', icon: '🗂️', category: 'Association' },
    { value: 'ORDONNANCEMENT', label: 'Ordonnancement', icon: '↕️', category: 'Association' },
    { value: 'REP_COURT', label: 'Réponse courte', icon: '✏️', category: 'Réponse libre' },
    { value: 'REP_LONG', label: 'Réponse longue', icon: '📄', category: 'Réponse libre' },
    { value: 'QUESTION_OUVERTE', label: 'Question ouverte', icon: '💬', category: 'Réponse libre' },
    { value: 'ZONE_TEXTE_RICHE', label: 'Zone de texte riche', icon: '🖋️', category: 'Réponse libre' },
    { value: 'REP_NUMERIQUE', label: 'Réponse numérique', icon: '🔢', category: 'Numérique / Code' },
    { value: 'CODE', label: 'Code', icon: '💻', category: 'Numérique / Code' },
	{ value: 'CLASSE', label: 'Classement (CLASSE)', icon: '📋', category: 'Association' },
	{ value: 'CATEGORI', label: 'Catégorisation', icon: '🏷️', category: 'Association' },
	{ value: 'DEPOT_FICHIER', label: 'Dépôt de fichier (oral, scan…)', icon: '📎', category: 'Réponse libre' }
];

// Types de média attachables à une question (l'énoncé/contenu à consulter,
// indépendant du type de question — cf. note en tête de fichier).
const MEDIA_TYPES = [
    { value: 'IMAGE', label: 'Image', icon: '🖼️' },
    { value: 'AUDIO', label: 'Audio', icon: '🎵' },
    { value: 'VIDEO', label: 'Vidéo', icon: '🎬' },
    { value: 'PDF', label: 'PDF', icon: '📄' }
];

function generateId(prefix) {
    return `${prefix}-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`;
}

function buildDefaultQuestion(type) {
    const base = {
        id: generateId('q'),
        type,
        enonce: '',
        points: 1,
        explication: '',
        feedbackAuto: AUTO_DEFAULT_TYPES.includes(type),
        mediaUrl: '',
        mediaType: ''
    };
    switch (type) {
        case 'CHOIX_UNIQUE':
        case 'CHOIX_MULTIPLE':
            return { ...base, options: ['Option A', 'Option B', 'Option C'], reponseAttendue: '0' };
        case 'VRAI_FAUX':
            return { ...base, options: ['Vrai', 'Faux'], reponseAttendue: '0' };
        case 'TEXTE_TROU':
            return { ...base, enonce: 'Complétez : le ___ est ___.', reponseAttendue: 'réponse1,réponse2' };
        case 'APPARIEMENT':
            return { ...base, appariement: { 'Élément 1': 'Valeur 1', 'Élément 2': 'Valeur 2' } };
        case 'CLASSEMENT':
            return { ...base, classement: { 'Catégorie A': ['Élément 1'], 'Catégorie B': ['Élément 2'] } };
        case 'ORDONNANCEMENT':
            return { ...base, ordreAttendu: ['Étape 1', 'Étape 2', 'Étape 3'] };
        case 'REP_COURT':
            return { ...base, reponseAttendue: '' };
        case 'REP_LONG':
        case 'QUESTION_OUVERTE':
        case 'ZONE_TEXTE_RICHE':
            return { ...base, reponseAttendue: '' };
        case 'REP_NUMERIQUE':
            return { ...base, reponseAttendue: '0', toleranceNumerique: 0 };
        case 'CODE':
            return { ...base, codeLangage: 'javascript', codeExecutable: false, reponseAttendue: '' };
		case 'CLASSE':
			return { ...base, classement: { 'Catégorie A': ['Élément 1'], 'Catégorie B': ['Élément 2'] } };
		case 'CATEGORI':
			return { ...base, appariement: { 'Élément 1': 'Catégorie A', 'Élément 2': 'Catégorie B' } };
		case 'DEPOT_FICHIER':
			return { ...base, metaDonnees: { maxFileSize: 5242880, acceptedTypes: ['audio/*'] } };	
			
        default:
            return base;
    }
}

export default function ConstruireEvaluationRich({
    evaluationId = null,
    clientId,
    token,
    initialData = null,
    onSave,
    onDelete,
    onPreview
}) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [showJsonPreview, setShowJsonPreview] = useState(false);
    const [expandedQuestions, setExpandedQuestions] = useState({});
    const [showImportModal, setShowImportModal] = useState(false);
    const [importJson, setImportJson] = useState('');
    const [importError, setImportError] = useState(null);

    const getDefaultEvaluation = useCallback(() => ({
        titre: 'Évaluation enrichie',
        code: `RICH-${Date.now()}-${Math.random().toString(36).substring(2, 6).toUpperCase()}`,
        description: '',
        type: 'SOMMATIVE',
        status: 'BROUILLON',
        publique: false,
        dateDebutInscription: null,
        dateFinInscription: null,
        scoreMinimum: 70,
        dureeMinutes: 30,
        dateDebut: null,
        dateFin: null,
        clientId: clientId,
        estObligatoire: true,
        feedbackAutoGlobal: true,
        moduleId: null,
        questions: [buildDefaultQuestion('CHOIX_UNIQUE')]
    }), [clientId]);

    const [evalData, setEvalData] = useState(() => {
        if (initialData) {
            const data = typeof initialData === 'string' ? JSON.parse(initialData) : initialData;
            if (!data.clientId && clientId) data.clientId = clientId;
            if (!Array.isArray(data.questions)) data.questions = [];
            return data;
        }
        return getDefaultEvaluation();
    });

    // Chargement d'une évaluation existante
    useEffect(() => {
        if (!evaluationId || !token) return;
        const fetchEvaluation = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await fetch(`/api/v1/admin/evaluations-rich/${evaluationId}`, {
                    headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
                });
                if (!response.ok) throw new Error(`Erreur HTTP ${response.status}`);
                const data = await response.json();
                setEvalData({
                    ...data,
                    clientId: clientId || data.clientId,
                    questions: (data.questions || []).map(q => ({ ...q, id: q.id || generateId('q') }))
                });
                setSuccess('Évaluation enrichie chargée avec succès');
            } catch (err) {
                setError(`Erreur de chargement : ${err.message}`);
            } finally {
                setLoading(false);
            }
        };
        fetchEvaluation();
    }, [evaluationId, token, clientId]);

    useEffect(() => {
        setEvalData(prev => (prev.clientId !== clientId ? { ...prev, clientId } : prev));
    }, [clientId]);

    // ============================================================
    // UTILITAIRES
    // ============================================================
    const getQuestionTypeMeta = (type) => QUESTION_TYPES_RICH.find(t => t.value === type) || { label: type, icon: '❓' };

    const toggleQuestionExpand = useCallback((qIndex) => {
        setExpandedQuestions(prev => ({ ...prev, [qIndex]: !(prev[qIndex] !== undefined ? prev[qIndex] : true) }));
    }, []);

    // ============================================================
    // GESTION DES QUESTIONS
    // ============================================================
    const addQuestion = useCallback((type = 'CHOIX_UNIQUE') => {
        const newQuestion = { ...buildDefaultQuestion(type), enonce: `Question ${evalData.questions.length + 1}` };
        setEvalData(prev => ({ ...prev, questions: [...prev.questions, newQuestion] }));
        setExpandedQuestions(prev => ({ ...prev, [evalData.questions.length]: true }));
    }, [evalData.questions.length]);

    const removeQuestion = useCallback((qIndex) => {
        if (!window.confirm('Supprimer cette question ?')) return;
        setEvalData(prev => ({ ...prev, questions: prev.questions.filter((_, idx) => idx !== qIndex) }));
    }, []);

    const updateQuestion = useCallback((qIndex, field, value) => {
        setEvalData(prev => ({
            ...prev,
            questions: prev.questions.map((q, idx) => (idx === qIndex ? { ...q, [field]: value } : q))
        }));
    }, []);

    const changeQuestionType = useCallback((qIndex, newType) => {
        setEvalData(prev => ({
            ...prev,
            questions: prev.questions.map((q, idx) => {
                if (idx !== qIndex) return q;
                const rebuilt = buildDefaultQuestion(newType);
                return {
                    ...rebuilt,
                    id: q.id,
                    enonce: q.enonce,
                    points: q.points,
                    explication: q.explication,
                    mediaUrl: q.mediaUrl,
                    mediaType: q.mediaType
                };
            })
        }));
    }, []);

    const moveQuestion = useCallback((qIndex, direction) => {
        setEvalData(prev => {
            const target = qIndex + direction;
            if (target < 0 || target >= prev.questions.length) return prev;
            const next = [...prev.questions];
            [next[qIndex], next[target]] = [next[target], next[qIndex]];
            return { ...prev, questions: next };
        });
    }, []);

    // ---- Options (CHOIX_UNIQUE / CHOIX_MULTIPLE) ----
    const updateOption = useCallback((qIndex, optIndex, value) => {
        setEvalData(prev => ({
            ...prev,
            questions: prev.questions.map((q, idx) => idx === qIndex
                ? { ...q, options: q.options.map((o, oi) => (oi === optIndex ? value : o)) }
                : q)
        }));
    }, []);

    const addOption = useCallback((qIndex) => {
        setEvalData(prev => ({
            ...prev,
            questions: prev.questions.map((q, idx) => idx === qIndex
                ? { ...q, options: [...(q.options || []), `Option ${(q.options || []).length + 1}`] }
                : q)
        }));
    }, []);

    const removeOption = useCallback((qIndex, optIndex) => {
        setEvalData(prev => {
            const q = prev.questions[qIndex];
            if ((q.options || []).length <= 2) {
                alert('Une question doit avoir au moins 2 options');
                return prev;
            }
            // Ajuste reponseAttendue (indices) après suppression
            let reponseAttendue = q.reponseAttendue;
            if (q.type === 'CHOIX_UNIQUE') {
                const cur = parseInt(reponseAttendue, 10);
                if (cur === optIndex) reponseAttendue = '0';
                else if (cur > optIndex) reponseAttendue = String(cur - 1);
            } else if (q.type === 'CHOIX_MULTIPLE') {
                const set = (reponseAttendue || '').split(',').map(s => parseInt(s, 10)).filter(n => !isNaN(n) && n !== optIndex)
                    .map(n => (n > optIndex ? n - 1 : n));
                reponseAttendue = [...new Set(set)].join(',');
            }
            return {
                ...prev,
                questions: prev.questions.map((qq, idx) => idx === qIndex
                    ? { ...qq, options: qq.options.filter((_, oi) => oi !== optIndex), reponseAttendue }
                    : qq)
            };
        });
    }, []);

    const setCorrectSingle = useCallback((qIndex, optIndex) => {
        updateQuestion(qIndex, 'reponseAttendue', String(optIndex));
    }, [updateQuestion]);

    const toggleCorrectMultiple = useCallback((qIndex, optIndex) => {
        setEvalData(prev => ({
            ...prev,
            questions: prev.questions.map((q, idx) => {
                if (idx !== qIndex) return q;
                const current = (q.reponseAttendue || '').split(',').map(s => s.trim()).filter(s => s !== '');
                const asStr = String(optIndex);
                const next = current.includes(asStr) ? current.filter(v => v !== asStr) : [...current, asStr];
                return { ...q, reponseAttendue: next.join(',') };
            })
        }));
    }, []);

    // ---- Texte à trous ----
    const getTrousList = (q) => (q.reponseAttendue ? q.reponseAttendue.split(',') : ['']);
    const updateTrou = useCallback((qIndex, trouIndex, value) => {
        setEvalData(prev => ({
            ...prev,
            questions: prev.questions.map((q, idx) => {
                if (idx !== qIndex) return q;
                const trous = getTrousList(q);
                trous[trouIndex] = value;
                return { ...q, reponseAttendue: trous.join(',') };
            })
        }));
    }, []);
    const addTrou = useCallback((qIndex) => {
        setEvalData(prev => ({
            ...prev,
            questions: prev.questions.map((q, idx) => idx === qIndex
                ? { ...q, reponseAttendue: [...getTrousList(q), ''].join(',') }
                : q)
        }));
    }, []);
    const removeTrou = useCallback((qIndex, trouIndex) => {
        setEvalData(prev => ({
            ...prev,
            questions: prev.questions.map((q, idx) => {
                if (idx !== qIndex) return q;
                const trous = getTrousList(q);
                if (trous.length <= 1) return q;
                return { ...q, reponseAttendue: trous.filter((_, i) => i !== trouIndex).join(',') };
            })
        }));
    }, []);

    // ---- Appariement ----
    const updateAppariementPair = useCallback((qIndex, oldKey, newKey, newValue) => {
        setEvalData(prev => ({
            ...prev,
            questions: prev.questions.map((q, idx) => {
                if (idx !== qIndex) return q;
                const entries = Object.entries(q.appariement || {}).map(([k, v]) =>
                    k === oldKey ? [newKey, newValue] : [k, v]
                );
                return { ...q, appariement: Object.fromEntries(entries) };
            })
        }));
    }, []);
    const addAppariementPair = useCallback((qIndex) => {
        setEvalData(prev => ({
            ...prev,
            questions: prev.questions.map((q, idx) => idx === qIndex
                ? { ...q, appariement: { ...(q.appariement || {}), [`Élément ${Object.keys(q.appariement || {}).length + 1}`]: '' } }
                : q)
        }));
    }, []);
    const removeAppariementPair = useCallback((qIndex, key) => {
        setEvalData(prev => ({
            ...prev,
            questions: prev.questions.map((q, idx) => {
                if (idx !== qIndex) return q;
                const entries = Object.entries(q.appariement || {}).filter(([k]) => k !== key);
                return { ...q, appariement: Object.fromEntries(entries) };
            })
        }));
    }, []);

    // ---- Classement ----
    const updateClassementCategorie = useCallback((qIndex, oldCat, newCat) => {
        setEvalData(prev => ({
            ...prev,
            questions: prev.questions.map((q, idx) => {
                if (idx !== qIndex) return q;
                const entries = Object.entries(q.classement || {}).map(([k, v]) => (k === oldCat ? [newCat, v] : [k, v]));
                return { ...q, classement: Object.fromEntries(entries) };
            })
        }));
    }, []);
    const addClassementCategorie = useCallback((qIndex) => {
        setEvalData(prev => ({
            ...prev,
            questions: prev.questions.map((q, idx) => idx === qIndex
                ? { ...q, classement: { ...(q.classement || {}), [`Catégorie ${Object.keys(q.classement || {}).length + 1}`]: [] } }
                : q)
        }));
    }, []);
    const removeClassementCategorie = useCallback((qIndex, cat) => {
        setEvalData(prev => ({
            ...prev,
            questions: prev.questions.map((q, idx) => {
                if (idx !== qIndex) return q;
                const entries = Object.entries(q.classement || {}).filter(([k]) => k !== cat);
                return { ...q, classement: Object.fromEntries(entries) };
            })
        }));
    }, []);
    const addClassementItem = useCallback((qIndex, cat) => {
        setEvalData(prev => ({
            ...prev,
            questions: prev.questions.map((q, idx) => idx === qIndex
                ? { ...q, classement: { ...q.classement, [cat]: [...(q.classement[cat] || []), `Élément ${(q.classement[cat] || []).length + 1}`] } }
                : q)
        }));
    }, []);
    const updateClassementItem = useCallback((qIndex, cat, itemIndex, value) => {
        setEvalData(prev => ({
            ...prev,
            questions: prev.questions.map((q, idx) => idx === qIndex
                ? { ...q, classement: { ...q.classement, [cat]: q.classement[cat].map((it, i) => (i === itemIndex ? value : it)) } }
                : q)
        }));
    }, []);
    const removeClassementItem = useCallback((qIndex, cat, itemIndex) => {
        setEvalData(prev => ({
            ...prev,
            questions: prev.questions.map((q, idx) => idx === qIndex
                ? { ...q, classement: { ...q.classement, [cat]: q.classement[cat].filter((_, i) => i !== itemIndex) } }
                : q)
        }));
    }, []);

    // ---- Ordonnancement ----
    const updateOrdreItem = useCallback((qIndex, itemIndex, value) => {
        setEvalData(prev => ({
            ...prev,
            questions: prev.questions.map((q, idx) => idx === qIndex
                ? { ...q, ordreAttendu: q.ordreAttendu.map((it, i) => (i === itemIndex ? value : it)) }
                : q)
        }));
    }, []);
    const addOrdreItem = useCallback((qIndex) => {
        setEvalData(prev => ({
            ...prev,
            questions: prev.questions.map((q, idx) => idx === qIndex
                ? { ...q, ordreAttendu: [...(q.ordreAttendu || []), `Étape ${(q.ordreAttendu || []).length + 1}`] }
                : q)
        }));
    }, []);
    const removeOrdreItem = useCallback((qIndex, itemIndex) => {
        setEvalData(prev => {
            const q = prev.questions[qIndex];
            if ((q.ordreAttendu || []).length <= 2) {
                alert("L'ordonnancement doit avoir au moins 2 éléments");
                return prev;
            }
            return {
                ...prev,
                questions: prev.questions.map((qq, idx) => idx === qIndex
                    ? { ...qq, ordreAttendu: qq.ordreAttendu.filter((_, i) => i !== itemIndex) }
                    : qq)
            };
        });
    }, []);
    const moveOrdreItem = useCallback((qIndex, itemIndex, direction) => {
        setEvalData(prev => {
            const q = prev.questions[qIndex];
            const target = itemIndex + direction;
            if (target < 0 || target >= q.ordreAttendu.length) return prev;
            const next = [...q.ordreAttendu];
            [next[itemIndex], next[target]] = [next[target], next[itemIndex]];
            return { ...prev, questions: prev.questions.map((qq, idx) => (idx === qIndex ? { ...qq, ordreAttendu: next } : qq)) };
        });
    }, []);

    // ============================================================
    // VALIDATION
    // ============================================================
    const validateEvaluation = useCallback((strict) => {
        const errors = [];
        const warnings = [];

        if (!evalData.titre || !evalData.titre.trim()) errors.push('Le titre est obligatoire.');
        if (!evalData.clientId && !clientId) errors.push('Le clientId est obligatoire.');
        if (!evalData.type) errors.push("Le type d'évaluation est obligatoire.");
        if (!evalData.questions || evalData.questions.length === 0) {
            (strict ? errors : warnings).push("L'évaluation ne contient aucune question.");
        }

        (evalData.questions || []).forEach((q, idx) => {
            const n = idx + 1;
            if (!q.enonce || !q.enonce.trim()) warnings.push(`Question ${n} : énoncé manquant.`);
            switch (q.type) {
                case 'CHOIX_UNIQUE':
                case 'CHOIX_MULTIPLE':
                    if (!q.options || q.options.length < 2) errors.push(`Question ${n} : au moins 2 options requises.`);
                    if (!q.reponseAttendue) warnings.push(`Question ${n} : aucune bonne réponse sélectionnée.`);
                    break;
                case 'VRAI_FAUX':
                    if (q.reponseAttendue === undefined || q.reponseAttendue === '') warnings.push(`Question ${n} : réponse Vrai/Faux non définie.`);
                    break;
                case 'TEXTE_TROU':
                    if (!q.reponseAttendue) warnings.push(`Question ${n} : réponses des trous manquantes.`);
                    break;
                case 'APPARIEMENT':
                    if (!q.appariement || Object.keys(q.appariement).length === 0) errors.push(`Question ${n} : appariement vide.`);
                    break;
                case 'CLASSEMENT': {
                    const cats = Object.keys(q.classement || {});
                    if (cats.length < 2) errors.push(`Question ${n} : au moins 2 catégories requises.`);
                    if (cats.some(c => (q.classement[c] || []).length === 0)) warnings.push(`Question ${n} : une catégorie est vide.`);
                    break;
                }
                case 'ORDONNANCEMENT':
                    if (!q.ordreAttendu || q.ordreAttendu.length < 2) errors.push(`Question ${n} : au moins 2 éléments à ordonner.`);
                    break;
                case 'REP_NUMERIQUE':
                    if (q.reponseAttendue === undefined || q.reponseAttendue === '' || isNaN(Number(q.reponseAttendue))) {
                        errors.push(`Question ${n} : réponse numérique attendue invalide.`);
                    }
                    break;
                case 'CODE':
                    if (q.feedbackAuto && (!q.reponseAttendue || !q.reponseAttendue.trim())) {
                        warnings.push(`Question ${n} : correction automatique activée mais aucun code attendu défini.`);
                    }
                    break;
                case 'REP_COURT':
                    if (q.feedbackAuto && (!q.reponseAttendue || !q.reponseAttendue.trim())) {
                        warnings.push(`Question ${n} : correction automatique activée mais aucune réponse attendue définie.`);
                    }
                    break;
                default:
                    break;
            }
        });

        return { errors, warnings };
    }, [evalData, clientId]);

    // ============================================================
    // SAUVEGARDE
    // ============================================================
    const handleSave = async () => {
        const effectiveClientId = evalData.clientId || clientId;
        if (!effectiveClientId) {
            const msg = '⚠️ Le clientId est manquant. Veuillez sélectionner un client pour cette évaluation.';
            setError(msg);
            alert(msg);
            return;
        }

        let codeToUse = evalData.code;
        if (!codeToUse || !codeToUse.trim()) {
            codeToUse = `RICH-${Date.now()}-${Math.random().toString(36).substring(2, 6).toUpperCase()}`;
            setEvalData(prev => ({ ...prev, code: codeToUse }));
        }

        const isStrict = evalData.status === 'PUBLIEE' || evalData.status === 'TERMINEE';
        const { errors, warnings } = validateEvaluation(isStrict);

        if (errors.length > 0) {
            const msg = `❌ ${errors.join('\n')}`;
            setError(msg);
            alert(msg);
            return;
        }
        if (warnings.length > 0 && evalData.status === 'BROUILLON') {
            const confirmMsg = `⚠️ Certaines questions sont incomplètes :\n${warnings.join('\n')}\n\nSouhaitez-vous quand même sauvegarder en brouillon ?`;
            if (!window.confirm(confirmMsg)) return;
        }

        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            const questions = evalData.questions.map(q => ({
                ...q,
                feedbackAuto: evalData.feedbackAutoGlobal ? true : !!q.feedbackAuto
            }));

            const payload = {
                titre: evalData.titre,
                code: codeToUse,
                description: evalData.description || '',
                type: evalData.type,
                status: evalData.status || 'BROUILLON',
                publique: !!evalData.publique,
                dateDebutInscription: evalData.dateDebutInscription || null,
                dateFinInscription: evalData.dateFinInscription || null,
                scoreMinimum: evalData.scoreMinimum ?? 70,
                dureeMinutes: evalData.dureeMinutes ?? 30,
                dateDebut: evalData.dateDebut || null,
                dateFin: evalData.dateFin || null,
                clientId: effectiveClientId,
                questions,
                estObligatoire: evalData.estObligatoire !== false,
                feedbackAutoGlobal: !!evalData.feedbackAutoGlobal,
                moduleId: evalData.moduleId || null
            };

            const method = evaluationId ? 'PUT' : 'POST';
            const url = evaluationId ? `/api/v1/admin/evaluations-rich/${evaluationId}` : '/api/v1/admin/evaluations-rich';

            const response = await fetch(url, {
                method,
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || errorData.message || `Erreur HTTP ${response.status}`);
            }

            const saved = await response.json();
            setEvalData(prev => ({ ...prev, ...saved, clientId: effectiveClientId }));
            setSuccess(`✅ Évaluation enrichie ${evaluationId ? 'mise à jour' : 'créée'} avec succès`);
            if (onSave) onSave(saved);
        } catch (err) {
            console.error('Erreur sauvegarde évaluation enrichie :', err);
            setError(`❌ Erreur lors de la sauvegarde : ${err.message}`);
            alert(`❌ Erreur : ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!evaluationId) return;
        if (!window.confirm('Supprimer définitivement cette évaluation enrichie ? Cette action est irréversible.')) return;
        setLoading(true);
        setError(null);
        setSuccess(null);
        try {
            const response = await fetch(`/api/v1/admin/evaluations-rich/${evaluationId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || `Erreur HTTP ${response.status}`);
            }
            setSuccess('✅ Évaluation enrichie supprimée');
            if (onDelete) onDelete(evaluationId);
        } catch (err) {
            setError(`❌ Erreur lors de la suppression : ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleChangeStatus = async (newStatus) => {
        if (!evaluationId) {
            setEvalData(prev => ({ ...prev, status: newStatus }));
            return;
        }
        setLoading(true);
        try {
            const response = await fetch(`/api/v1/admin/evaluations-rich/${evaluationId}/status?status=${newStatus}`, {
                method: 'PATCH',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error(`Erreur HTTP ${response.status}`);
            setEvalData(prev => ({ ...prev, status: newStatus }));
            setSuccess(`✅ Statut changé en ${newStatus}`);
        } catch (err) {
            setError(`❌ Erreur lors du changement de statut : ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handlePreview = useCallback(() => {
        const { errors } = validateEvaluation(false);
        if (errors.length > 0) {
            setError(errors.join('\n'));
            return;
        }
        if (onPreview) onPreview(evalData);
    }, [evalData, onPreview, validateEvaluation]);

    const handleExportJson = () => {
        const dataToExport = { ...evalData, clientId: evalData.clientId || clientId };
        const blob = new Blob([JSON.stringify(dataToExport, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `evaluation-rich-${evalData.code || 'export'}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        setSuccess('✅ Évaluation enrichie exportée en JSON');
        setTimeout(() => setSuccess(null), 3000);
    };

    const handleImportJson = () => {
        try {
            const parsed = JSON.parse(importJson);
            if (!parsed.titre || !Array.isArray(parsed.questions)) {
                throw new Error('Le JSON doit contenir "titre" et "questions" (tableau).');
            }
            parsed.clientId = parsed.clientId || clientId;
            parsed.questions = parsed.questions.map(q => ({ id: q.id || generateId('q'), ...q }));
            setEvalData(prev => ({ ...prev, ...parsed }));
            setShowImportModal(false);
            setImportJson('');
            setImportError(null);
            setSuccess("✅ JSON importé avec succès. N'oubliez pas de sauvegarder !");
            setTimeout(() => setSuccess(null), 4000);
        } catch (err) {
            setImportError(err.message);
        }
    };

    // ============================================================
    // ÉDITEURS SPÉCIFIQUES PAR TYPE
    // ============================================================
    const renderChoixEditor = (q, qIndex, multiple) => {
        const correctSingle = q.reponseAttendue !== undefined ? parseInt(q.reponseAttendue, 10) : -1;
        const correctMultiple = (q.reponseAttendue || '').split(',').map(s => s.trim()).filter(s => s !== '');
        return (
            <div className="space-y-2">
                <label className="block text-xs font-medium text-gray-700">Options {multiple ? '(cochez toutes les bonnes réponses)' : '(sélectionnez la bonne réponse)'}</label>
                {(q.options || []).map((opt, oi) => (
                    <div key={oi} className="flex items-center gap-2">
                        <input
                            type={multiple ? 'checkbox' : 'radio'}
                            name={`correct-${q.id}`}
                            checked={multiple ? correctMultiple.includes(String(oi)) : correctSingle === oi}
                            onChange={() => (multiple ? toggleCorrectMultiple(qIndex, oi) : setCorrectSingle(qIndex, oi))}
                            className="w-4 h-4 flex-shrink-0"
                        />
                        <input
                            type="text"
                            value={opt}
                            onChange={(e) => updateOption(qIndex, oi, e.target.value)}
                            className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                        />
                        <button onClick={() => removeOption(qIndex, oi)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg">
                            <Trash2 size={14} />
                        </button>
                    </div>
                ))}
                <button onClick={() => addOption(qIndex)} className="text-xs text-green-700 hover:text-green-900 flex items-center gap-1">
                    <Plus size={12} /> Ajouter une option
                </button>
            </div>
        );
    };

    const renderVraiFauxEditor = (q, qIndex) => (
        <div className="space-y-2">
            <label className="block text-xs font-medium text-gray-700">Réponse correcte</label>
            {['Vrai', 'Faux'].map((label, oi) => (
                <label key={oi} className="flex items-center gap-2 text-sm">
                    <input
                        type="radio"
                        name={`vf-${q.id}`}
                        checked={parseInt(q.reponseAttendue, 10) === oi}
                        onChange={() => setCorrectSingle(qIndex, oi)}
                        className="w-4 h-4"
                    />
                    {label}
                </label>
            ))}
        </div>
    );

    const renderTexteTrouEditor = (q, qIndex) => (
        <div className="space-y-2">
            <p className="text-[10px] text-gray-500">
                Utilisez <code className="bg-gray-100 px-1 rounded">___</code> dans l'énoncé pour marquer chaque trou, puis renseignez les réponses attendues dans l'ordre.
            </p>
            <label className="block text-xs font-medium text-gray-700">Réponses attendues (dans l'ordre des trous)</label>
            {getTrousList(q).map((trou, ti) => (
                <div key={ti} className="flex items-center gap-2">
                    <span className="text-xs text-gray-400 w-14 flex-shrink-0">Trou {ti + 1}</span>
                    <input
                        type="text"
                        value={trou}
                        onChange={(e) => updateTrou(qIndex, ti, e.target.value)}
                        className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                    <button onClick={() => removeTrou(qIndex, ti)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg">
                        <Trash2 size={14} />
                    </button>
                </div>
            ))}
            <button onClick={() => addTrou(qIndex)} className="text-xs text-green-700 hover:text-green-900 flex items-center gap-1">
                <Plus size={12} /> Ajouter un trou
            </button>
        </div>
    );

    const renderAppariementEditor = (q, qIndex) => (
        <div className="space-y-2">
            <label className="block text-xs font-medium text-gray-700">Paires clé → valeur attendue</label>
            {Object.entries(q.appariement || {}).map(([key, value], pi) => (
                <div key={pi} className="flex items-center gap-2">
                    <input
                        type="text"
                        value={key}
                        onChange={(e) => updateAppariementPair(qIndex, key, e.target.value, value)}
                        placeholder="Élément"
                        className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                    <span className="text-gray-400">→</span>
                    <input
                        type="text"
                        value={value}
                        onChange={(e) => updateAppariementPair(qIndex, key, key, e.target.value)}
                        placeholder="Valeur attendue"
                        className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                    <button onClick={() => removeAppariementPair(qIndex, key)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg">
                        <Trash2 size={14} />
                    </button>
                </div>
            ))}
            <button onClick={() => addAppariementPair(qIndex)} className="text-xs text-green-700 hover:text-green-900 flex items-center gap-1">
                <Plus size={12} /> Ajouter une paire
            </button>
        </div>
    );

    const renderClassementEditor = (q, qIndex) => (
        <div className="space-y-3">
            <label className="block text-xs font-medium text-gray-700">Catégories et éléments à classer</label>
            {Object.entries(q.classement || {}).map(([cat, items], ci) => (
                <div key={ci} className="p-2 border border-gray-200 rounded-lg space-y-1.5">
                    <div className="flex items-center gap-2">
                        <input
                            type="text"
                            value={cat}
                            onChange={(e) => updateClassementCategorie(qIndex, cat, e.target.value)}
                            className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-green-500"
                        />
                        <button onClick={() => removeClassementCategorie(qIndex, cat)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg">
                            <Trash2 size={14} />
                        </button>
                    </div>
                    <div className="pl-3 space-y-1">
                        {(items || []).map((item, ii) => (
                            <div key={ii} className="flex items-center gap-2">
                                <input
                                    type="text"
                                    value={item}
                                    onChange={(e) => updateClassementItem(qIndex, cat, ii, e.target.value)}
                                    className="flex-1 px-3 py-1 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-green-500"
                                />
                                <button onClick={() => removeClassementItem(qIndex, cat, ii)} className="p-1 text-red-400 hover:text-red-600">
                                    <Trash2 size={12} />
                                </button>
                            </div>
                        ))}
                        <button onClick={() => addClassementItem(qIndex, cat)} className="text-[11px] text-green-700 hover:text-green-900 flex items-center gap-1">
                            <Plus size={10} /> Élément
                        </button>
                    </div>
                </div>
            ))}
            <button onClick={() => addClassementCategorie(qIndex)} className="text-xs text-green-700 hover:text-green-900 flex items-center gap-1">
                <Plus size={12} /> Ajouter une catégorie
            </button>
        </div>
    );

    const renderOrdonnancementEditor = (q, qIndex) => (
        <div className="space-y-2">
            <label className="block text-xs font-medium text-gray-700">Ordre attendu (de haut en bas)</label>
            {(q.ordreAttendu || []).map((item, ii) => (
                <div key={ii} className="flex items-center gap-2">
                    <span className="text-xs text-gray-400 w-5">{ii + 1}.</span>
                    <input
                        type="text"
                        value={item}
                        onChange={(e) => updateOrdreItem(qIndex, ii, e.target.value)}
                        className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                    <button onClick={() => moveOrdreItem(qIndex, ii, -1)} disabled={ii === 0} className="p-1 text-gray-500 hover:text-gray-800 disabled:opacity-30">
                        <ArrowUp size={14} />
                    </button>
                    <button onClick={() => moveOrdreItem(qIndex, ii, 1)} disabled={ii === (q.ordreAttendu || []).length - 1} className="p-1 text-gray-500 hover:text-gray-800 disabled:opacity-30">
                        <ArrowDown size={14} />
                    </button>
                    <button onClick={() => removeOrdreItem(qIndex, ii)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg">
                        <Trash2 size={14} />
                    </button>
                </div>
            ))}
            <button onClick={() => addOrdreItem(qIndex)} className="text-xs text-green-700 hover:text-green-900 flex items-center gap-1">
                <Plus size={12} /> Ajouter un élément
            </button>
        </div>
    );

    const renderTexteLibreEditor = (q, qIndex, label) => (
        <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
            <textarea
                value={q.reponseAttendue || ''}
                onChange={(e) => updateQuestion(qIndex, 'reponseAttendue', e.target.value)}
                rows={3}
                className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="Optionnel — utile pour guider la correction manuelle"
            />
        </div>
    );

    const renderNumeriqueEditor = (q, qIndex) => (
        <div className="grid grid-cols-2 gap-3">
            <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Réponse numérique attendue</label>
                <input
                    type="number"
                    step="any"
                    value={q.reponseAttendue ?? ''}
                    onChange={(e) => updateQuestion(qIndex, 'reponseAttendue', e.target.value)}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                />
            </div>
            <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Tolérance (±)</label>
                <input
                    type="number"
                    step="any"
                    min="0"
                    value={q.toleranceNumerique ?? 0}
                    onChange={(e) => updateQuestion(qIndex, 'toleranceNumerique', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                />
            </div>
        </div>
    );

    const renderCodeEditor = (q, qIndex) => (
        <div className="space-y-2">
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Langage</label>
                    <select
                        value={q.codeLangage || 'javascript'}
                        onChange={(e) => updateQuestion(qIndex, 'codeLangage', e.target.value)}
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                        {CODE_LANGUAGES.map(lang => <option key={lang} value={lang}>{lang}</option>)}
                    </select>
                </div>
                <div className="flex items-end pb-1.5">
                    <label className="flex items-center gap-2 text-xs text-gray-700">
                        <input
                            type="checkbox"
                            checked={!!q.codeExecutable}
                            onChange={(e) => updateQuestion(qIndex, 'codeExecutable', e.target.checked)}
                        />
                        Exécuter le code soumis (sandbox)
                    </label>
                </div>
            </div>
            <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Code / solution attendue</label>
                <textarea
                    value={q.reponseAttendue || ''}
                    onChange={(e) => updateQuestion(qIndex, 'reponseAttendue', e.target.value)}
                    rows={6}
                    spellCheck={false}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs font-mono bg-gray-900 text-green-300 focus:outline-none focus:ring-2 focus:ring-green-500"
                />
                <p className="text-[10px] text-amber-600 mt-1 flex items-center gap-1">
                    <HelpCircle size={11} /> La correction automatique du code compare le texte (espaces ignorés) — préférez la correction manuelle pour une évaluation fiable.
                </p>
            </div>
        </div>
    );
	
	const renderFichierEditor = (q, qIndex) => {
	    const meta = q.metaDonnees || {};
	    return (
	        <div className="space-y-2">
	            <label className="block text-xs font-medium text-gray-700">Contraintes du fichier</label>
	            <div className="grid grid-cols-2 gap-2">
	                <div>
	                    <label className="block text-xs text-gray-600">Taille max (octets)</label>
	                    <input
	                        type="number"
	                        value={meta.maxFileSize || 5242880}
	                        onChange={(e) => {
	                            const val = parseInt(e.target.value, 10) || 0;
	                            updateQuestion(qIndex, 'metaDonnees', { ...meta, maxFileSize: val });
	                        }}
	                        className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
	                    />
	                </div>
	                <div>
	                    <label className="block text-xs text-gray-600">Types acceptés (ex: audio/*, image/*)</label>
	                    <input
	                        type="text"
	                        value={(meta.acceptedTypes || ['audio/*']).join(', ')}
	                        onChange={(e) => {
	                            const types = e.target.value.split(',').map(s => s.trim()).filter(s => s);
	                            updateQuestion(qIndex, 'metaDonnees', { ...meta, acceptedTypes: types });
	                        }}
	                        className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
	                        placeholder="audio/*, video/*"
	                    />
	                </div>
	            </div>
	        </div>
	    );
	};
	
    const renderTypeSpecificFields = (q, qIndex) => {
        switch (q.type) {
            case 'CHOIX_UNIQUE': return renderChoixEditor(q, qIndex, false);
            case 'CHOIX_MULTIPLE': return renderChoixEditor(q, qIndex, true);
            case 'VRAI_FAUX': return renderVraiFauxEditor(q, qIndex);
            case 'TEXTE_TROU': return renderTexteTrouEditor(q, qIndex);
            case 'APPARIEMENT': return renderAppariementEditor(q, qIndex);
            case 'CLASSEMENT': return renderClassementEditor(q, qIndex);
            case 'ORDONNANCEMENT': return renderOrdonnancementEditor(q, qIndex);
            case 'REP_COURT': return renderTexteLibreEditor(q, qIndex, 'Réponse exacte attendue (comparaison insensible à la casse)');
            case 'REP_LONG':
            case 'QUESTION_OUVERTE':
            case 'ZONE_TEXTE_RICHE':
                return renderTexteLibreEditor(q, qIndex, 'Réponse modèle (référence pour la correction manuelle)');
            case 'REP_NUMERIQUE': return renderNumeriqueEditor(q, qIndex);
            case 'CODE': return renderCodeEditor(q, qIndex);
			case 'CLASSE':    return renderClassementEditor(q, qIndex);
			case 'CATEGORI':    return renderAppariementEditor(q, qIndex);
			case 'DEPOT_FICHIER':    return renderFichierEditor(q, qIndex);
            default: return null;
        }
    };

    // ============================================================
    // RENDU D'UNE QUESTION
    // ============================================================
    const renderQuestionForm = (question, qIndex) => {
        const isExpanded = expandedQuestions[qIndex] !== undefined ? expandedQuestions[qIndex] : true;
        const meta = getQuestionTypeMeta(question.type);

        return (
            <div key={question.id || qIndex} className="border border-gray-200 rounded-lg mb-3 overflow-hidden">
                <div
                    className="bg-gray-50 px-4 py-2.5 flex items-center justify-between cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => toggleQuestionExpand(qIndex)}
                >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                        <span className="text-lg flex-shrink-0">{meta.icon}</span>
                        <span className="font-semibold text-gray-700 truncate">
                            Question {qIndex + 1}: {question.enonce || 'Sans énoncé'}
                        </span>
                        <span className="text-xs text-gray-400 flex-shrink-0">{meta.label}</span>
                        {!!question.points && (
                            <span className="text-xs text-gray-400 flex-shrink-0">{question.points} pt{question.points > 1 ? 's' : ''}</span>
                        )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => moveQuestion(qIndex, -1)} disabled={qIndex === 0} className="p-1.5 text-gray-400 hover:text-gray-700 disabled:opacity-30">
                            <ArrowUp size={14} />
                        </button>
                        <button onClick={() => moveQuestion(qIndex, 1)} disabled={qIndex === evalData.questions.length - 1} className="p-1.5 text-gray-400 hover:text-gray-700 disabled:opacity-30">
                            <ArrowDown size={14} />
                        </button>
                        <button onClick={() => removeQuestion(qIndex)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg">
                            <Trash2 size={14} />
                        </button>
                        <span onClick={() => toggleQuestionExpand(qIndex)}>
                            {isExpanded ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronRight size={16} className="text-gray-400" />}
                        </span>
                    </div>
                </div>

                {isExpanded && (
                    <div className="p-4 space-y-3">
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Énoncé *</label>
                            <textarea
                                value={question.enonce || ''}
                                onChange={(e) => updateQuestion(qIndex, 'enonce', e.target.value)}
                                className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                                rows="2"
                                placeholder="Texte de la question"
                            />
                        </div>

                        <div className="p-3 bg-fuchsia-50 rounded-lg border border-fuchsia-200">
                            <label className="block text-xs font-semibold text-fuchsia-700 mb-2">
                                🎞️ Média attaché à l'énoncé (optionnel)
                            </label>
                            <p className="text-[10px] text-fuchsia-600 mb-2">
                                Fait écouter/voir/lire un contenu à l'étudiant avant qu'il réponde — indépendant
                                du type de question choisi ci-dessous (ex. un audio + un Choix unique).
                            </p>
                            <div className="flex flex-wrap gap-1.5 mb-2">
                                <button
                                    type="button"
                                    onClick={() => updateQuestion(qIndex, 'mediaType', '')}
                                    className={`text-xs px-3 py-1.5 rounded-full border transition ${
                                        !question.mediaType
                                            ? 'bg-gray-700 text-white border-gray-700'
                                            : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                                    }`}
                                >
                                    Aucun
                                </button>
                                {MEDIA_TYPES.map(mt => (
                                    <button
                                        key={mt.value}
                                        type="button"
                                        onClick={() => updateQuestion(qIndex, 'mediaType', mt.value)}
                                        className={`text-xs px-3 py-1.5 rounded-full border transition flex items-center gap-1 ${
                                            question.mediaType === mt.value
                                                ? 'bg-fuchsia-600 text-white border-fuchsia-600'
                                                : 'bg-white text-gray-600 border-gray-300 hover:bg-fuchsia-50'
                                        }`}
                                    >
                                        <span>{mt.icon}</span> {mt.label}
                                    </button>
                                ))}
                            </div>
                            {question.mediaType && (
                                <input
                                    type="text"
                                    value={question.mediaUrl || ''}
                                    onChange={(e) => updateQuestion(qIndex, 'mediaUrl', e.target.value)}
                                    placeholder={`URL du fichier ${question.mediaType.toLowerCase()} (ex: https://.../fichier)`}
                                    className="w-full px-3 py-1.5 border border-fuchsia-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-fuchsia-500"
                                />
                            )}
                            {question.mediaType && !question.mediaUrl && (
                                <p className="text-[10px] text-amber-600 mt-1">
                                    ⚠️ Type de média choisi mais aucune URL renseignée : rien ne s'affichera à l'étudiant.
                                </p>
                            )}
                        </div>

                        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                            <label className="block text-xs font-semibold text-blue-700 mb-1">🔄 Type de question</label>
                            <select
                                value={question.type}
                                onChange={(e) => changeQuestionType(qIndex, e.target.value)}
                                className="w-full px-3 py-2 border border-blue-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-green-500"
                            >
                                {Object.entries(
                                    QUESTION_TYPES_RICH.reduce((acc, t) => {
                                        (acc[t.category] = acc[t.category] || []).push(t);
                                        return acc;
                                    }, {})
                                ).map(([cat, types]) => (
                                    <optgroup key={cat} label={cat}>
                                        {types.map(t => <option key={t.value} value={t.value}>{t.icon} {t.label}</option>)}
                                    </optgroup>
                                ))}
                            </select>
                        </div>

                        <div className="p-3 border border-gray-200 rounded-lg">
                            {renderTypeSpecificFields(question, qIndex)}
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Points</label>
                                <input
                                    type="number"
                                    min="1"
                                    value={question.points || 1}
                                    onChange={(e) => updateQuestion(qIndex, 'points', parseInt(e.target.value, 10) || 1)}
                                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                                />
                            </div>
                            <div className="flex items-end pb-1.5">
                                <label className={`flex items-center gap-2 text-xs ${evalData.feedbackAutoGlobal ? 'text-gray-400' : 'text-gray-700'}`}>
                                    <input
                                        type="checkbox"
                                        checked={evalData.feedbackAutoGlobal ? true : !!question.feedbackAuto}
                                        disabled={!!evalData.feedbackAutoGlobal}
                                        onChange={(e) => updateQuestion(qIndex, 'feedbackAuto', e.target.checked)}
                                    />
                                    Correction automatique
                                    {evalData.feedbackAutoGlobal && <span className="italic">(imposée globalement)</span>}
                                </label>
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Explication / Feedback</label>
                            <textarea
                                value={question.explication || ''}
                                onChange={(e) => updateQuestion(qIndex, 'explication', e.target.value)}
                                className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                                rows="2"
                                placeholder="Explication affichée après correction (optionnelle)"
                            />
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // ============================================================
    // RENDU PRINCIPAL
    // ============================================================
    return (
        <div className="space-y-4">
            {/* En-tête */}
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                    <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                        <ClipboardList size={24} className="text-purple-500" />
                        Évaluation enrichie
                        {evaluationId && <span className="text-sm text-gray-400 font-normal">(ID: {evaluationId})</span>}
                        {(evalData.clientId || clientId) && <span className="text-xs text-gray-400 ml-2">Client ID: {evalData.clientId || clientId}</span>}
                    </h2>
                    <p className="text-xs text-gray-500 mt-1">
                        16 types de questions disponibles (choix, texte à trous, appariement, classement, ordonnancement, code, réponses libres, numériques et dépôt de fichier). Chaque question peut aussi afficher un média (audio, image, vidéo, PDF) indépendant de son type.
                    </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    {evaluationId && (
                        <button onClick={handleDelete} disabled={loading} className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50">
                            🗑️ Supprimer
                        </button>
                    )}
                    <button onClick={() => setShowJsonPreview(!showJsonPreview)} className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg border border-gray-300">
                        {showJsonPreview ? '📝 Éditer' : '👁️ Prévisualiser JSON'}
                    </button>
                    <button onClick={handleExportJson} className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg border border-gray-300">
                        ⬇️ Exporter JSON
                    </button>
                    <button onClick={() => setShowImportModal(true)} className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg border border-gray-300">
                        📥 Importer JSON
                    </button>
                    <button onClick={handlePreview} className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors flex items-center gap-1">
                        <Eye size={16} /> Démo
                    </button>
                    <button onClick={handleSave} disabled={loading} className="px-3 py-1.5 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1">
                        <Save size={16} /> {loading ? 'Sauvegarde...' : 'Sauvegarder'}
                    </button>
                </div>
            </div>

            {/* Messages */}
            {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 whitespace-pre-line flex items-start justify-between gap-2">
                    <span>{error}</span>
                    <button onClick={() => setError(null)}><X size={14} /></button>
                </div>
            )}
            {success && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 flex items-start justify-between gap-2">
                    <span>{success}</span>
                    <button onClick={() => setSuccess(null)}><X size={14} /></button>
                </div>
            )}

            {showJsonPreview ? (
                <pre className="p-4 bg-gray-900 text-green-300 rounded-lg text-xs overflow-auto max-h-[600px]">
                    {JSON.stringify(evalData, null, 2)}
                </pre>
            ) : (
                <>
                    {/* Informations générales */}
                    <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Titre *</label>
                                <input
                                    type="text"
                                    value={evalData.titre || ''}
                                    onChange={(e) => setEvalData(prev => ({ ...prev, titre: e.target.value }))}
                                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Code</label>
                                <input
                                    type="text"
                                    value={evalData.code || ''}
                                    onChange={(e) => setEvalData(prev => ({ ...prev, code: e.target.value }))}
                                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                                    placeholder="Généré automatiquement si vide"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                            <textarea
                                value={evalData.description || ''}
                                onChange={(e) => setEvalData(prev => ({ ...prev, description: e.target.value }))}
                                rows={2}
                                className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                            />
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
                                <select
                                    value={evalData.type}
                                    onChange={(e) => setEvalData(prev => ({ ...prev, type: e.target.value }))}
                                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-green-500"
                                >
                                    {EVAL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Statut</label>
                                <select
                                    value={evalData.status}
                                    onChange={(e) => handleChangeStatus(e.target.value)}
                                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-green-500"
                                >
                                    {EVAL_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Score minimum (%)</label>
                                <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    value={evalData.scoreMinimum ?? 70}
                                    onChange={(e) => setEvalData(prev => ({ ...prev, scoreMinimum: parseInt(e.target.value, 10) || 0 }))}
                                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Durée (min)</label>
                                <input
                                    type="number"
                                    min="1"
                                    value={evalData.dureeMinutes ?? 30}
                                    onChange={(e) => setEvalData(prev => ({ ...prev, dureeMinutes: parseInt(e.target.value, 10) || 1 }))}
                                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Date de début</label>
                                <input
                                    type="datetime-local"
                                    value={evalData.dateDebut ? evalData.dateDebut.substring(0, 16) : ''}
                                    onChange={(e) => setEvalData(prev => ({ ...prev, dateDebut: e.target.value || null }))}
                                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Date de fin</label>
                                <input
                                    type="datetime-local"
                                    value={evalData.dateFin ? evalData.dateFin.substring(0, 16) : ''}
                                    onChange={(e) => setEvalData(prev => ({ ...prev, dateFin: e.target.value || null }))}
                                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Début inscription</label>
                                <input
                                    type="date"
                                    value={evalData.dateDebutInscription || ''}
                                    onChange={(e) => setEvalData(prev => ({ ...prev, dateDebutInscription: e.target.value || null }))}
                                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Fin inscription</label>
                                <input
                                    type="date"
                                    value={evalData.dateFinInscription || ''}
                                    onChange={(e) => setEvalData(prev => ({ ...prev, dateFinInscription: e.target.value || null }))}
                                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                                />
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-4 pt-1">
                            <label className="flex items-center gap-2 text-xs text-gray-700">
                                <input type="checkbox" checked={!!evalData.publique} onChange={(e) => setEvalData(prev => ({ ...prev, publique: e.target.checked }))} />
                                Publique
                            </label>
                            <label className="flex items-center gap-2 text-xs text-gray-700">
                                <input type="checkbox" checked={evalData.estObligatoire !== false} onChange={(e) => setEvalData(prev => ({ ...prev, estObligatoire: e.target.checked }))} />
                                Obligatoire
                            </label>
                            <label className="flex items-center gap-2 text-xs text-gray-700">
                                <input
                                    type="checkbox"
                                    checked={!!evalData.feedbackAutoGlobal}
                                    onChange={(e) => setEvalData(prev => ({ ...prev, feedbackAutoGlobal: e.target.checked }))}
                                />
                                Correction automatique pour toutes les questions
                            </label>
                        </div>
                    </div>

                    {/* Liste des questions */}
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                            <h3 className="text-sm font-bold text-gray-800">
                                Questions ({evalData.questions.length})
                            </h3>
                            <div className="flex items-center gap-2 flex-wrap">
                                {QUESTION_TYPES_RICH.map(t => (
                                    <button
                                        key={t.value}
                                        onClick={() => addQuestion(t.value)}
                                        title={`Ajouter : ${t.label}`}
                                        className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg flex items-center gap-1"
                                    >
                                        <Plus size={10} /> {t.icon} {t.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {evalData.questions.length === 0 ? (
                            <p className="text-sm text-gray-400 italic text-center py-6">Aucune question. Ajoutez-en une ci-dessus.</p>
                        ) : (
                            evalData.questions.map((q, idx) => renderQuestionForm(q, idx))
                        )}
                    </div>
                </>
            )}

            {/* Modal d'import JSON */}
            {showImportModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-4 space-y-3">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-bold text-gray-800">Importer un JSON</h3>
                            <button onClick={() => { setShowImportModal(false); setImportError(null); }}>
                                <X size={18} className="text-gray-400" />
                            </button>
                        </div>
                        <textarea
                            value={importJson}
                            onChange={(e) => setImportJson(e.target.value)}
                            rows={10}
                            placeholder='{"titre": "...", "questions": [...] }'
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs font-mono focus:outline-none focus:ring-2 focus:ring-green-500"
                        />
                        {importError && <p className="text-xs text-red-600">{importError}</p>}
                        <div className="flex justify-end gap-2">
                            <button onClick={() => { setShowImportModal(false); setImportError(null); }} className="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded-lg">
                                Annuler
                            </button>
                            <button onClick={handleImportJson} className="px-3 py-1.5 text-sm text-white bg-green-600 hover:bg-green-700 rounded-lg">
                                Importer
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}